"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { defaultLocale, type Locale, getLocaleFromPathname } from "@/lib/i18n";
import { getPageTranslation, getTranslation } from "@/lib/translations";
import { Button } from "@/components/ui/button";
import { Settings, CircuitBoard, Monitor } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi
} from "@/components/ui/carousel";

// Import images
import hardwareEngineeringImg from "@/assets/homepage/hardware-engineering-cad-new.jpg";
import schaltschrankbauImg from "@/assets/homepage/schaltschrankbau.jpg";
import softwarePlcImg from "@/assets/homepage/software-engineering-new.jpg";

interface ServicesSectionProps {
  locale?: Locale;
  subtitle?: string;
  hideHeader?: boolean;
}

const serviceKeys = ['schaltschrankbau', 'hardware', 'software'] as const;

const serviceData = {
  schaltschrankbau: {
    icon: Settings,
    image: schaltschrankbauImg,
    route: "schaltschrankbau",
  },
  hardware: {
    icon: CircuitBoard,
    image: hardwareEngineeringImg,
    route: "engineering",
  },
  software: {
    icon: Monitor,
    image: softwarePlcImg,
    route: "softwareengineering",
  },
};

export function ServicesSection({ locale, subtitle, hideHeader = false }: ServicesSectionProps) {
  const location = useLocation();
  const currentLocale = locale || getLocaleFromPathname(location.pathname);
  const pt = getPageTranslation(currentLocale);
  const t = getTranslation(currentLocale);
  const servicesPath = currentLocale === defaultLocale 
    ? `/${t.routes.services}` 
    : `/${currentLocale}/${t.routes.services}`;
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    api.on("select", () => setCurrent(api.selectedScrollSnap()));
  }, [api]);

  const renderCard = (serviceKey: typeof serviceKeys[number], index: number) => {
    const service = serviceData[serviceKey];
    const serviceTranslation = pt.servicesSection.services[serviceKey];
    const Icon = service.icon;
    const localizedServiceRoute = t.routes[service.route as keyof typeof t.routes] || service.route;
    const servicePath = `${servicesPath}/${localizedServiceRoute}`;

    return (
      <Link to={servicePath} className="block w-full h-full">
        <div className="group relative overflow-hidden rounded-xl aspect-[4/5] md:aspect-[3/4] lg:aspect-[4/5] cursor-pointer">
          <img
            src={service.image}
            alt={serviceTranslation.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
          <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
            <h3 className="text-xl md:text-lg lg:text-2xl font-normal tracking-wide text-white pr-6">
              {serviceTranslation.title}
            </h3>
          </div>
        </div>
      </Link>
    );
  };

  return (
    <section className={`${hideHeader ? "pt-2 md:pt-4" : "pt-16 md:pt-24"} pb-6 md:pb-8 bg-white dark:bg-background`}>
      {!hideHeader && (
        <>
          <div className="hidden md:block px-4 md:px-8">
            <div className="mx-auto lg:max-w-[calc(66.6666%+1.5rem)]">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <h2 className="text-3xl md:text-[45px] font-normal text-foreground leading-relaxed tracking-wide mb-4">
                  {pt.servicesSection.title}
                </h2>
                <p className="text-lg md:text-[22px] font-extralight text-foreground/80 leading-relaxed tracking-wide">
                  {subtitle || pt.servicesSection.subtitle}
                </p>
              </motion.div>
            </div>
          </div>
          <div className="md:hidden container mx-auto px-4 max-w-6xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-[45px] font-normal text-foreground leading-relaxed tracking-wide mb-4">
                {pt.servicesSection.title}
              </h2>
              <p className="text-lg md:text-[22px] font-extralight text-foreground/80 leading-relaxed tracking-wide">
                {subtitle || pt.servicesSection.subtitle}
              </p>
            </motion.div>
          </div>
        </>
      )}


      {/* Mobile: Carousel */}
      <div className="md:hidden px-4">
        <Carousel setApi={setApi} opts={{ loop: true }} className="w-full">
          <CarouselContent>
            {serviceKeys.map((serviceKey, index) => (
              <CarouselItem key={serviceKey} className="basis-[85%] pl-4">
                {renderCard(serviceKey, index)}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        {/* Pagination Dots */}
        <div className="flex justify-center gap-2 mt-4">
          {serviceKeys.map((_, index) => (
            <button
              key={index}
              onClick={() => api?.scrollTo(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === current ? "w-6 bg-primary" : "w-2 bg-primary/30"
              }`}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Desktop: Grid */}
      <div className="hidden md:block px-4 md:px-8">
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {serviceKeys.map((serviceKey, index) => (
            <motion.div
              key={serviceKey}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {renderCard(serviceKey, index)}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}